import React, { useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router-dom";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-theme-material.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserTag,
  faUsersRays,
  faUserPlus,
  faUserMinus,
  faEye,
  faPenNib,
  faTrash,
  faSearch,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

const Clientslist = () => {
  const navigate = useNavigate();

  const gridRef = useRef();
  const [rowData, setRowData] = useState();

  // counts
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);

  // search
  const [searchText, setSearchText] = useState("");

  // confirm dialog
  const [confirm, setConfirm] = useState({
    open: false,
    item: null,
    loading: false,
  });

  const columnDefs = [
    {
      field: "id",
      headerName: "Id",
      valueGetter: "node.rowIndex+1",
      width: "100",
    },
    { field: "name", headerName: "Name" },
    { field: "domain", headerName: "Domain" },
    { field: "redirect_url", headerName: "Redirect Url" },
    { field: "email", headerName: "Email" },
    {
      headerName: "Action",
      width: "250px",
      cellRenderer: (rowData) => (
        <div className="flex space-y-6 md:space-x-2 md:space-y-0">
          <button
            className="py-1 px-1 mr-1 rounded bg-blue-500 text-sm hover:bg-blue-600 ease-in-out duration-300 text-white"
            onClick={() => handleViewAction(rowData.data)}
          >
            <FontAwesomeIcon icon={faEye} className="text-white font-medium" />
          </button>
          <button
            className="py-1 px-1 mr-1 rounded bg-green-400 text-sm hover:bg-green-600 ease-in-out duration-300 text-white"
            onClick={() => handleEditAction(rowData.data)}
          >
            <FontAwesomeIcon icon={faPenNib} className="text-white font-medium" />
          </button>
          <button
            className="py-1 px-1 rounded bg-red-400 hover:bg-red-600 text-sm ease-in-out duration-300 text-white"
            onClick={() => openConfirm(rowData.data)}
          >
            <FontAwesomeIcon icon={faTrash} className="text-white font-medium" />
          </button>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    sortable: true,
    flex: 1,
    minWidth: 100,
    filter: true,
  };

  useEffect(() => {
    const isAuth = JSON.parse(localStorage.getItem("access_token"));
    if (!isAuth) {
      navigate("/");
      return;
    }
    getData();
  }, [navigate]);

  const getData = async () => {
    fetch(`${process.env.REACT_APP_API_URL}/clients/all`, {
      method: "get",
      headers: new Headers({
        "Content-Type": "application/json",
        authorization:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
      }),
    })
      .then((result) => result.json())
      .then((payload) => {
        // set grid rows
        setRowData(payload.data);

        // counts
        const items = payload?.data || [];
        const total = payload?.meta_data?.total ?? items.length;
        const active = items.filter((x) => x?.active === true).length;
        const inactive = Math.max(total - active, 0);

        setTotalCount(total);
        setActiveCount(active);
        setInactiveCount(inactive);
      });
  };

  // search handlers
  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setQuickFilter(val);
    }
  };

  const clearSearch = () => {
    setSearchText("");
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setQuickFilter("");
    }
  };

  // confirm modal helpers
  const openConfirm = (item) =>
    setConfirm({ open: true, item, loading: false });
  const closeConfirm = () =>
    setConfirm({ open: false, item: null, loading: false });

  // delete with modal
  const doDelete = () => {
    if (!confirm?.item?.id) return;
    setConfirm((c) => ({ ...c, loading: true }));

    fetch(`${process.env.REACT_APP_API_URL}/clients/delete/${confirm.item.id}`, {
      method: "delete",
      headers: new Headers({
        "Content-Type": "application/json",
        authorization:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
      }),
      // body can be omitted if your API doesn't need it for delete
      // body: JSON.stringify({}),
    })
      .then((result) => result.json())
      .then(() => {
        closeConfirm();
        getData();
      })
      .catch(() => setConfirm((c) => ({ ...c, loading: false })));
  };

  // close modal on Esc / confirm with Enter
  useEffect(() => {
    if (!confirm.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeConfirm();
      if (e.key === "Enter") doDelete();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm.open]); // this is fine

  const handleEditAction = (data) => {
    navigate(`/clientsEdit/${data.id}`);
  };

  const handleViewAction = (data) => {
    navigate(`/clientsView/${data.id}`);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col items-start justify-between pb-6 space-y-4 lg:items-center lg:space-y-0 lg:flex-row">
        <h1 className="text-2xl font-semibold whitespace-nowrap">
          <FontAwesomeIcon icon={faUserTag} className="text-grey-900" /> Clients
          List
        </h1>
        <div className="space-y-6 md:space-x-2 md:space-y-0">
          <a
            href="/clientsAdd"
            className="inline-flex items-center justify-center px-4 py-1 space-x-1 bg-green-500 text-white rounded-md shadow animate-bounce hover:bg-green-600"
          >
            <span>Add Client</span>
          </a>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 mt-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="p-2 transition-shadow border rounded-lg shadow-sm hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex flex-col space-y-2">
              <span className="text-400 font-medium">Total Clients</span>
              <span className="text-lg font-semibold text-blue-600 text-5xl">
                {totalCount}
              </span>
            </div>
            <div className="p-5">
              <FontAwesomeIcon
                icon={faUsersRays}
                className="text-5xl text-blue-400"
              />
            </div>
          </div>
        </div>

        <div className="p-2 transition-shadow border rounded-lg shadow-sm hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex flex-col space-y-2">
              <span className="text-400 font-medium">Active Clients</span>
              <span className="text-lg font-semibold text-green-600 text-5xl">
                {activeCount}
              </span>
            </div>
            <div className="p-5">
              <FontAwesomeIcon
                icon={faUserPlus}
                className="text-5xl text-green-400"
              />
            </div>
          </div>
        </div>

        <div className="p-2 transition-shadow border rounded-lg shadow-sm hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex flex-col space-y-2">
              <span className="text-400 font-medium">In-Active Clients</span>
              <span className="text-lg font-semibold text-red-600 text-5xl">
                {inactiveCount}
              </span>
            </div>
            <div className="p-5">
              <FontAwesomeIcon
                icon={faUserMinus}
                className="text-5xl text-red-400"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Search + Grid */}
      <div className="border p-1 mt-3 border-grey-200 rounded-md shadow-lg">
        {/* Search bar (right aligned) */}
        <div className="flex items-center justify-end p-2">
          <div className="relative ml-auto w-full max-w-sm md:max-w-md">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              value={searchText}
              onChange={onSearchChange}
              placeholder="Search clients (name, email, domain, redirect url)…"
              className="w-full rounded-md border border-gray-300 pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchText ? (
              <button
                type="button"
                title="Clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={clearSearch}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            ) : null}
          </div>
        </div>

        <div
          className="ag-theme-material overflow-hidden"
          style={{ height: 500, marginTop: 8 }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={10}
            paginationAutoPageSize={true}
          />
        </div>
      </div>

      {/* Confirm Modal */}
      {confirm.open && (
        <div
          className="confirm-modal fixed inset-0 z-50 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeConfirm}
          />
          {/* Card */}
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-black/5">
            {/* Header */}
            <div className="px-6 pt-6">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                  <FontAwesomeIcon icon={faTrash} className="text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete client?
                </h3>
              </div>
              <p className="mt-3 text-sm text-gray-600">
                You are about to delete{" "}
                <span className="font-medium text-gray-900">
                  “{confirm.item?.name || "this client"}”
                </span>
                . This action cannot be undone.
              </p>
            </div>

            {/* Footer */}
            <div className="modal-actions mt-6 flex items-center justify-end gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={closeConfirm}
                className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={confirm.loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                className="inline-flex items-center rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-700"
              >
                {confirm.loading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    Deleting…
                  </span>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientslist;
